/**
 * Holds a video the visitor picked on the landing page BEFORE they had an
 * account, so the upload can start automatically the moment they're logged in.
 *
 * Two layers:
 *  - In-memory: survives SPA route changes (landing → /register → /app).
 *  - IndexedDB: survives full page loads and new tabs, which is the path
 *    email-signup users take (register → verification link opens a new tab).
 *
 * Entries expire after PENDING_TTL_MS so a file picked days ago doesn't
 * silently upload on some later login.
 */

const DB_NAME = 'desicaptions';
const STORE = 'pendingUpload';
const KEY = 'current';
const PENDING_TTL_MS = 60 * 60 * 1000; // 1 hour

let memory = null;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(entry) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry, KEY);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  }));
}

function idbGet() {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => { db.close(); resolve(req.result || null); };
    req.onerror = () => { db.close(); reject(req.error); };
  }));
}

function idbDelete() {
  return openDb().then((db) => new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); resolve(); };
  }));
}

function isFresh(entry) {
  return entry && entry.file instanceof Blob && Date.now() - entry.savedAt < PENDING_TTL_MS;
}

/** Save the picked file + chosen caption config. IndexedDB write is best-effort. */
export async function savePendingUpload(file, config) {
  const entry = { file, config, savedAt: Date.now() };
  memory = entry;
  try { await idbPut(entry); } catch { /* private mode etc. — memory still works */ }
}

/** Returns { file, config } or null. Does NOT clear — call clearPendingUpload once consumed. */
export async function loadPendingUpload() {
  if (isFresh(memory)) return memory;
  try {
    const entry = await idbGet();
    if (isFresh(entry)) return entry;
    if (entry) idbDelete(); // stale — clean up
  } catch { /* ignore */ }
  return null;
}

export async function hasPendingUpload() {
  return (await loadPendingUpload()) !== null;
}

export function clearPendingUpload() {
  memory = null;
  idbDelete();
}
